import { DateTimeFormatter, ZonedDateTime, ZoneId } from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import styled, { css } from 'styled-components';
import { AvailabilityCalendarTimeFragment } from '../../graphql/types';
import { AQUAMARINE, BLACK2, HOVER, WHITE } from '../../theme';
import { time } from '../../utils/date';
import LoadingPanel from '../LoadingPanel';
import Text, { TextKind } from '../reusable/Text';

const StyledSelectableItem = styled.button<{ $selected: boolean }>`
  cursor: pointer;
  box-sizing: border-box;
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  text-align: center;

  &:hover {
    background: ${HOVER};
  }

  ${({ $selected }) => css`
    border: 1px solid ${$selected ? AQUAMARINE : BLACK2};
    background: ${$selected ? AQUAMARINE : WHITE};
  `}
`;

const SelectableItem = ({
  selected,
  start,
  end,
  onClick,
}: {
  selected?: boolean;
  start: ZonedDateTime;
  end: ZonedDateTime;
  onClick: () => void;
}) => {
  const formattedStart = time(start);
  const formattedEnd = time(end);

  const zone = start.format(
    DateTimeFormatter.ofPattern('z').withLocale(Locale.US),
  );

  return (
    <StyledSelectableItem
      type="button"
      data-selected={Boolean(selected)}
      onClick={onClick}
      $selected={Boolean(selected)}
      data-testid="selectableTime"
    >
      <Text kind={TextKind.LabelL}>
        {formattedStart} - {formattedEnd} ({zone})
      </Text>
    </StyledSelectableItem>
  );
};

interface ListTimesForDayProps {
  timezone: string;
  availability: AvailabilityCalendarTimeFragment[];
  selectedTime?: ZonedDateTime;
  onChange: (selectedStartTime: ZonedDateTime) => void;
  loading: boolean;
}
export const ListTimesForDay = ({
  availability,
  timezone,
  selectedTime,
  onChange,
  loading,
}: ListTimesForDayProps) => {
  const zone = ZoneId.of(timezone);

  return (
    <div
      style={{
        height: 286,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {loading && <LoadingPanel testId="selectableTimeList-loading" noBorder />}
      {availability.length === 0 && !loading && (
        <Text>No available times.</Text>
      )}
      {availability.map((node) => {
        const startTime = ZonedDateTime.parse(node.start);
        return (
          <SelectableItem
            key={node.start.toString()}
            selected={selectedTime?.isEqual(startTime)}
            start={startTime.withZoneSameInstant(zone)}
            end={ZonedDateTime.parse(node.end).withZoneSameInstant(zone)}
            onClick={() => onChange(startTime)}
          />
        );
      })}
    </div>
  );
};
